import { AbstractControl, ValidatorFn } from '@angular/forms';

export class AppValidators {

  static get notZeroNumber(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      return control.value === 0 && control.value !== undefined ? { 'invalidNotZeroNumber': { value: control.value } } : null;
    };
  }

  static get urlValidator(): ValidatorFn {
    const urlRegex = /^(^|\s)((https?:\/\/)?[\w-]+(\.[\w-]+)+\.?(:\d+)?(\/\S*)?)$/i;
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = !urlRegex.test(control.value);
      return forbidden && control.value ? { 'invalidUrl': { value: control.value } } : null;
    };
  }

  static get ibanValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      return !AppValidators.isValidIBANNumber(control.value) && control.value ? { 'invalidIban': { value: control.value } } : null;
    };
  }

  static get currencyCodeValidator(): ValidatorFn {
    const currencyCodeRegex = /^[A-Z]{3}$/;
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = !currencyCodeRegex.test(control.value);
      return forbidden && control.value ? { 'invalidCurrencyCode': { value: control.value } } : null;
    };
  }

  static get phoneNumberValidator(): ValidatorFn {
    const phoneNumberRegex = /^(\+\d{1,3})\d{8,9}$/;
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = !phoneNumberRegex.test(control.value);
      return forbidden && control.value ? { 'invalidPhoneNumber': { value: control.value } } : null;
    };
  }

  static get customerIdentifierValidator(): ValidatorFn {
    const customerIdentifierRegex = /^(\d{8}|\d{6}(\/|)\d{3,4})$/;
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = !customerIdentifierRegex.test(control.value);
      return forbidden && control.value ? { 'invalidCustomerIdentifier': { value: control.value } } : null;
    };
  }

  static get bankAccountNumberValidator(): ValidatorFn {
    const customerIdentifierRegex = /^\d+(\/|)\d+$/;
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = !customerIdentifierRegex.test(control.value);
      return forbidden && control.value ? { 'invalidBankAccountNumber': { value: control.value } } : null;
    };
  }

  static get zipCodeValidator(): ValidatorFn {
    const zipCodeRegex = /^\d+$/;
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = !zipCodeRegex.test(control.value);
      return forbidden && control.value ? { 'invalidZipCode': { value: control.value } } : null;
    };
  }

  static get floatValidator(): ValidatorFn {
    const floatRegex = /^[-+]?\d*\.?\d*$/;
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = !floatRegex.test(control.value);
      return forbidden && control.value ? { 'invalidFloat': { value: control.value } } : null;
    };
  }

  static passwordValidator(minLevel: string): ValidatorFn {
    const levels: {[s: string]: (score: number) => boolean} = {
      'weak': (score: number) => (score < 30),
      'medium': (score: number) => (score >= 30),
      'strong': (score: number) => (score >= 80)
    };

    if (!(minLevel in levels)) {
      throw Error('Unknown password level' + minLevel);
    }

    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null) {
        return null;
      }
  
      return !levels[minLevel](this.testStrength(control.value)) && control.value ? { 'invalidPasswordLevel': { value: control.value } } : null;
    };
  }

  static mod97(value: string) {
    let checksum = parseInt(value.slice(0, 2), 10);
    let fragment: string;
    for (let offset = 2; offset < value.length; offset += 7) {
      fragment = String(checksum) + value.substring(offset, offset + 7);
      checksum = parseInt(fragment, 10) % 97;
    }
    return checksum;
  }

  static isValidIBANNumber(input: string) {
    const CODE_LENGTHS: {[s: string]: number} = {
      AD: 24, AE: 23, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
      CH: 21, CR: 21, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, ES: 24,
      FI: 18, FO: 18, FR: 27, GB: 22, GI: 23, GL: 18, GR: 27, GT: 28, HR: 21,
      HU: 28, IE: 22, IL: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
      LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27,
      MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29,
      RO: 24, RS: 22, SA: 24, SE: 24, SI: 19, SK: 24, SM: 27, TN: 24, TR: 26
    };
    const iban = String(input).toUpperCase().replace(/[^A-Z0-9]/g, '');
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
    let digits: string;
    // check syntax and length
    if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
      return false;
    }
    // rearrange country code and check digits, and convert chars to ints
    digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter) => {
      return String(letter.charCodeAt(0) - 55);
    });
    // final check
    return AppValidators.mod97(digits);
  }

  static testStrength(str: string) {
        let grade: number = 0;
        let val: any;

        val = str.match('[0-9]');
        grade += this.normalize(val ? val.length : 1 / 4, 1) * 25;

        val = str.match('[a-zA-Z]');
        grade += this.normalize(val ? val.length : 1 / 2, 3) * 10;

        val = str.match('[!@#$%^&*?_~.,;=]');
        grade += this.normalize(val ? val.length : 1 / 6, 1) * 35;

        val = str.match('[A-Z]');
        grade += this.normalize(val ? val.length : 1 / 6, 1) * 30;

        grade *= str.length / 8;

        return grade > 100 ? 100 : grade;
    }

    static normalize(x: number, y: number) {
        const diff = x - y;

        if (diff <= 0) {
          return x / y;
        } else {
          return 1 + 0.5 * (x / (x + y / 4));
        }
    }

}
